import { createRouter, createWebHistory } from "vue-router";
import Raffles from "../views/Raffles/RafflesList/Raffles.vue";
import NewRaffle from "../views/Raffles/RafflesCreation/NewRaffle.vue";
import RafflePayment from "../views/Raffles/RafflesCreation/RafflePayment.vue";
import Settings from "../views/Settings/Settings.vue";
import SignIn from "../views/SignIn.vue";
import SignUp from "../views/SignUp.vue";
import Ranking from "../views/Ranking/Ranking.vue";
import Orders from "../views/Orders/Orders.vue";
import ValidateUser from '../views/ValidateUser.vue';
import Users from '../views/Users/Users.vue';
import SystemLogs from '../views/SystemLogs/SystemLogs.vue';
import SemaphoreControl from '../views/SemaphoreControl/SemaphoreControl.vue';
import { RouteName } from "../store/routeName";
import ResetPassword from "../views/ResetPassword.vue";
import LowestAndHighestQuotas from "../views/LowestAndHighest/LowestAndHighestQuotas.vue";
import Wallet from "../views/Wallet/Wallet.vue";
import Dashboard from "../views/Dashboard/DashboardEntry.vue";
import LogService from "../services/LogService";
import store from "../store/index";

// Mapeamento de rota → [MÓDULO, descrição amigável]
const ROUTE_LOG_MAP = {
  "Dashboard": ["DASHBOARD", "o painel principal (Dashboard)"],
  "Campanhas": ["CAMPANHAS", "a lista de campanhas"],
  "Nova campanha": ["CAMPANHAS", "a página de criação de campanha"],
  "Editar campanha": ["CAMPANHAS", "a página de edição de campanha"],
  "Pagamento de campanhas": ["CAMPANHAS", "a página de pagamento de campanha"],
  "Ranking": ["RANKING", "a página de ranking"],
  "Usuários": ["USUÁRIOS", "a página de gerenciamento de usuários"],
  "Logs do Sistema": ["SISTEMA", "a página de logs do sistema"],
  "Controle Semáforo": ["SISTEMA", "a página de controle do semáforo MySQL"],
  "Histórico de clientes": ["PEDIDOS", "a página de pedidos (histórico de clientes)"],
  "Cupons": ["SISTEMA", "a página de gerenciamento de cupons"],
  "Recuperar cadastros": ["USUÁRIOS", "a página de recuperação de cadastros"],
  "Disparo de e-mail": ["SISTEMA", "a página de disparo em massa de e-mail"],
  "Novidades": ["SISTEMA", "a página de novidades do painel"],
  "Maior e menor cota": ["CAMPANHAS", "a página de maior e menor cota"],
  "Jogos Premiados": ["CAMPANHAS", "bilhete, caixinha, roleta e raspadinha premiada"],
  "Cotas Premiadas": ["CAMPANHAS", "a página de cotas premiadas"],
  "Hora Premiada": ["CAMPANHAS", "a página de Hora Premiada"],
  "Sorteador": ["CAMPANHAS", "a página do Sorteador"],
  "Tutoriais": ["SUPORTE", "a página de tutoriais"],
  "Academy": ["SUPORTE", "a loja Academy"],
  "Gestão Academy": ["SISTEMA", "a gestão da Academy"],
  "Gestão de Tutoriais": ["SISTEMA", "a gestão de tutoriais"],
  "Planejador 365": ["CAMPANHAS", "a página do Planejador 365"],
  "Premiações": ["DASHBOARD", "a página de Premiações por faturamento"],
  "Configurações": ["CONFIGURAÇÕES", "a página de configurações da conta"],
  "Configurações Globais": ["CONFIGURAÇÕES", "a página de configurações globais do sistema"],
  "Notas Fiscais": ["SISTEMA", "a página de notas fiscais (Spedy)"],
  "Carteira": ["FINANCEIRO", "a carteira financeira"],
  "Solicitação de Carteira": ["FINANCEIRO", "a solicitação de Carteira"],
  "Solicitações de Carteira": ["FINANCEIRO", "a gestão de solicitações de Carteira"],
  "Meus Indicados": ["AFILIADOS", "a página de afiliados (Meus Indicados)"],
  "Gestão de Saques": ["FINANCEIRO", "a página de gestão de saques"],
  "Saques Afiliados": ["FINANCEIRO", "a página de saques de afiliados da plataforma"],
  "Marketplace de Afiliação": ["AFILIADOS", "o marketplace de afiliações"],
  "Seja Afiliado": ["AFILIADOS", "a página de gerenciar afiliações"],
};

// Rotas que NÃO devem gerar log de acesso (login, cadastro, validação)
const SKIP_LOG_ROUTES = new Set(["SignIn", "SignUp", "NovaSenha", "Validar", "/"]);

const routes = [
  {
    path: "/",
    name: "/",
    redirect: "/dashboard",
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
  },
  {
    path: "/raffles",
    name: "Campanhas",
    component: Raffles,
  },
  {
    path: "/raffles/new-raffle",
    name: RouteName.NewRaffle,
    component: NewRaffle,
  },
  {
    path: "/raffles/:slug/edit",
    name: RouteName.EditRaffle,
    component: NewRaffle,
  },
  {
    path: "/raffles/pay/:slug",
    name: 'Pagamento de campanhas',
    component: RafflePayment,
  },
  {
    path: "/ranking",
    name: "Ranking",
    component: Ranking,
  },
  {
    path: "/users",
    name: "Usuários",
    component: Users,
  },
  {
    path: "/system-logs",
    name: "Logs do Sistema",
    component: SystemLogs,
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/semaphore-control",
    name: "Controle Semáforo",
    component: SemaphoreControl,
  },
  {
    path: "/orders",
    name: "Histórico de clientes",
    component: Orders,
  },
  {
    path: "/coupons",
    name: "Cupons",
    component: () => import("../views/Coupons/Coupons.vue"),
  },
  {
    path: "/recovery",
    name: "Recuperar cadastros",
    component: () => import("../views/Recovery/RecoveryUsers.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/email-broadcast",
    name: "Disparo de e-mail",
    component: () => import("../views/Broadcast/EmailBroadcast.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/tracking",
    name: "Tracking avançado",
    component: () => import("../views/Tracking/TrackingAdvanced.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/tracking365",
    name: "Tracking365 Events",
    component: () => import("../views/Tracking/Tracking365Events.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/trafego",
    name: "Tracking 365",
    component: () => import("../views/Tracking/TrackingClient.vue"),
    meta: {
      requiresAuth: true,
      roles: ["USER"],
    },
  },
  {
    path: "/announcements",
    name: "Novidades",
    component: () => import("../views/Announcements/Announcements.vue"),
  },
  {
    path: "/admin/blog",
    name: "Blog SEO",
    component: () => import("../views/Blog/BlogAdmin.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/custom-domains",
    name: "Domínios",
    component: () => import("../views/Domains/CustomDomains.vue"),
  },
  {
    path: "/lowest-and-highest",
    name: "Maior e menor cota",
    component: LowestAndHighestQuotas,
  },
  {
    path: "/awarded-quotas",
    name: "Jogos Premiados",
    component: () => import("../views/AwardedQuotas/AwardedQuotas.vue"),
  },
  {
    path: "/hora-premiada",
    name: "Hora Premiada",
    component: () => import("../views/HoraPremiada/HoraPremiada.vue"),
  },
  {
    path: "/sorteador",
    name: "Sorteador",
    component: () => import("../views/Sorteador/Sorteador.vue"),
  },
  {
    path: "/planejador-365",
    name: "Planejador 365",
    component: () => import("../views/Planejador365/Planejador365.vue"),
  },
  {
    path: "/premiacoes",
    name: "Premiações",
    component: () => import("../views/Premiacoes/Premiacoes.vue"),
  },
  {
    path: "/Settings",
    name: "Configurações",
    component: Settings,
  },
  {
    path: "/global-settings",
    name: "Configurações Globais",
    component: () => import("../views/Settings/GlobalSettings.vue"),
  },
  {
    path: "/legal-documents",
    name: "Termos e Privacidade",
    component: () => import("../views/Settings/LegalDocuments.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/fiscal-invoices",
    name: "Notas Fiscais",
    component: () => import("../views/FiscalInvoices/FiscalInvoices.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/sign-in",
    name: "SignIn",
    component: SignIn,
  },
  {
    path: "/nova-senha/:token",
    name: "NovaSenha",
    component: ResetPassword,
  },
  {
    path: "/black-friday/pix",
    name: "BlackFridayPix",
    component: () => import("../views/Plans/BlackFridayPix.vue"),
  },
  {
    path: "/sign-up",
    name: "SignUp",
    component: SignUp,
  },
  {
    path: "/validar/:code",
    name: "Validar",
    component: ValidateUser,
  },
  {
    path: "/wallet",
    name: "Carteira",
    component: Wallet,
  },
  {
    path: "/wallet/application",
    name: "Solicitação de Carteira",
    component: () => import("../views/Wallet/InfluencerApplication.vue"),
    meta: {
      requiresAuth: true,
      roles: ["USER"],
    },
  },
  {
    path: "/wallet-applications",
    name: "Solicitações de Carteira",
    component: () => import("../views/Wallet/InfluencerApplicationsAdmin.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/meus-indicados",
    name: "Meus Indicados",
    component: () => import("../views/Affiliates/AffiliateDashboard.vue"),
  },
  {
    path: "/materiais-divulgacao",
    name: "Materiais de divulgação",
    component: () => import("../views/Affiliates/AffiliatePromoMaterials.vue"),
  },
  {
    path: "/admin/materiais-divulgacao",
    name: "Materiais afiliados",
    component: () => import("../views/Affiliates/AffiliatePromoMaterialsAdmin.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/admin/affiliates",
    name: "Afiliados",
    component: () => import("../views/Affiliates/AffiliatesAdmin.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/withdrawals-admin",
    name: "Gestão de Saques",
    component: () => import("../views/Withdrawals/Withdrawals.vue"),
  },
  {
    path: "/plan-payments",
    name: "PIX de planos",
    component: () => import("../views/Plans/PlanPayments.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/affiliate-withdrawals-admin",
    name: "Saques Afiliados",
    component: () => import("../views/Withdrawals/AffiliateWithdrawals.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/marketplace",
    redirect: "/minhas-afiliacoes",
  },
  {
    path: "/minhas-afiliacoes",
    name: "Seja Afiliado",
    component: () => import("../views/Affiliates/MyAffiliations.vue"),
  },
  {
    path: "/tutoriais",
    name: "Tutoriais",
    component: () => import("../views/Tutorials/Tutorials.vue"),
  },
  {
    path: "/academy",
    name: "Academy",
    component: () => import("../views/Academy/Academy.vue"),
  },
  {
    path: "/academy/:slug",
    name: "Academy Produto",
    component: () => import("../views/Academy/AcademyProduct.vue"),
  },
  {
    path: "/admin/academy",
    name: "Gestão Academy",
    component: () => import("../views/Academy/AcademyAdmin.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/admin/tutoriais",
    name: "Gestão de Tutoriais",
    component: () => import("../views/Tutorials/TutorialsAdmin.vue"),
    meta: {
      requiresAuth: true,
      roles: ["ADMIN"],
    },
  },
  {
    path: "/plans",
    name: "Planos",
    component: () => import("../views/Plans/Plans.vue"),
  },
  {
    path: "/plans/pagamento",
    name: "Pagamento Plano",
    component: () => import("../views/Plans/PlanPayment.vue"),
  },
  {
    path: "/remarketing",
    name: "Remarketing",
    component: () => import("../views/Remarketing/Remarketing.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

// ── Rotas públicas que não precisam de autenticação ─────────────────────────
const PUBLIC_ROUTES = new Set(['SignIn', 'SignUp', 'Validar', 'NovaSenha', 'BlackFridayPix']);

// ── Guard de autenticação (roda ANTES de entrar em qualquer rota) ────────────
router.beforeEach((to, from, next) => {
  const isPublic = PUBLIC_ROUTES.has(to.name);

  if (isPublic) {
    // Rota pública: deixa passar sem verificar token
    next();
    return;
  }

  // Rota protegida: verifica token no localStorage
  const token = localStorage.getItem('token');
  if (!token) {
    next('/sign-in');
    return;
  }

  const allowedRoles = to.meta?.roles;
  if (Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    const user = store.getters['user'];
    if (!user || !allowedRoles.includes(user.role)) {
      next('/dashboard');
      return;
    }
  }

  next();
});

// ── Log de navegação de rotas ────────────────────────────────────────────────
router.afterEach((to) => {
  try {
    const routeName = to.name;

    // Ignorar rotas de auth/validação
    if (!routeName || SKIP_LOG_ROUTES.has(routeName)) return;

    // pega user do vuex
    const user = store.getters['user']

    if (!user?.id) return; // Não logar se não está autenticado

    const userName = user.full_name || user.email || `ID ${user.id}`;
    const mapping = ROUTE_LOG_MAP[routeName];

    if (mapping) {
      const [module, pageDesc] = mapping;
      LogService.log(module, "ACESSO", `${userName} acessou ${pageDesc}`);
    }
  } catch {
    // Falha silenciosa — nunca bloquear navegação por causa de log
  }
});

export default router;
